<template>
  <!-- Shopper / Business Selector -->
  <div class="relative" :class="isBusiness ? 'bg-brand-800' : 'bg-brand-100'">
    <div class="px-6 lg:px-24">
      <div class="relative flex" role="tablist" aria-label="Mode selection">
        <button
          ref="shoppersButtonRef"
          @click="setActiveTab('shoppers')"
          class="relative z-10 px-4 py-3 font-semibold transition-all duration-200 hover:cursor-pointer"
          :class="isBusiness ? 'text-base-white' : 'text-brand-800'"
          role="tab"
          :aria-selected="activeTab === 'shoppers'"
          :tabindex="activeTab === 'shoppers' ? 0 : -1"
        >
          For Shoppers
        </button>

        <button
          ref="businessesButtonRef"
          @click="setActiveTab('businesses')"
          class="relative z-10 px-4 py-3 font-semibold transition-all duration-200 hover:cursor-pointer"
          :class="isBusiness ? 'text-base-white' : 'text-brand-800'"
          role="tab"
          :aria-selected="activeTab === 'businesses'"
          :tabindex="activeTab === 'businesses' ? 0 : -1"
        >
          For Business
        </button>

        <!-- Sliding Indicator Bar -->
        <div class="absolute bottom-0 transition-all duration-500 h-[3px]" :style="indicatorStyles" aria-hidden="true">
          <div class="h-full rounded-full" :class="isBusiness ? 'bg-brand-600' : 'bg-brand-800'"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useThemeStore } from '@/stores/theme';
import type { ComputedRef, CSSProperties, Ref } from 'vue';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const activeTab = ref<'shoppers' | 'businesses'>('shoppers');
const indicatorPosition = ref<number>(0);
const indicatorWidth = ref<number>(0);

const shoppersButtonRef = ref<HTMLButtonElement | null>(null);
const businessesButtonRef = ref<HTMLButtonElement | null>(null);

const themeStore = useThemeStore();

// Check if we're on business route
const isBusiness = computed(() => themeStore.mode === 'business');

// Computed styles for the indicator
const indicatorStyles: ComputedRef<CSSProperties> = computed(() => ({
  transform: `translateX(${indicatorPosition.value}px)`,
  width: `${indicatorWidth.value}px`,
  transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
}));

const updateIndicator = async (): Promise<void> => {
  await nextTick();

  const buttonRefs: Record<'shoppers' | 'businesses', Ref<HTMLButtonElement | null>> = {
    shoppers: shoppersButtonRef,
    businesses: businessesButtonRef,
  };

  const currentButtonRef = buttonRefs[activeTab.value];

  if (!currentButtonRef.value) return;

  if (activeTab.value === 'shoppers') {
    indicatorPosition.value = 0;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  } else {
    // For businesses tab, position is the width of the shoppers button
    const shoppersWidth = shoppersButtonRef.value?.offsetWidth || 0;
    indicatorPosition.value = shoppersWidth;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  }
};

const setActiveTab = (tab: 'shoppers' | 'businesses'): void => {
  activeTab.value = tab;
  updateIndicator();

  // Navigate to appropriate section
  if (tab === 'shoppers') {
    themeStore.setMode('shopper');
    // Navigate to shopper home if currently on business route
    if (route.path.startsWith('/business')) {
      router.push('/layby');
    }
  } else {
    themeStore.setMode('business');
    // Navigate to business home
    router.push('/business');
  }
};

// Watch for route changes to update active tab
watch(
  () => route.path,
  (newPath) => {
    if (newPath.startsWith('/business')) {
      activeTab.value = 'businesses';
    } else {
      activeTab.value = 'shoppers';
    }
    updateIndicator();
  },
  { immediate: true },
);

onMounted((): void => {
  updateIndicator();
  window.addEventListener('resize', updateIndicator);
});

onUnmounted((): void => {
  window.removeEventListener('resize', updateIndicator);
});
</script>
